/* Custom hero slider
   Loaded once with defer, shared across every instance of the section.

   Slides are stacked, not scrolled. Advancing draws the incoming slide on top
   of the outgoing one and animates its clip-path from a zero-width edge to the
   full frame, so the new artwork wipes across the old one while its media eases
   out of a slight zoom. Because nothing scrolls there are no loop clones and no
   scroll-snap settling: wrapping past either end is plain arithmetic. */

(function () {
  'use strict';

  if (window.__customHeroSliderLoaded) return;
  window.__customHeroSliderLoaded = true;

  var reduceMotion = window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  var mobileQuery = window.matchMedia
    ? window.matchMedia('(max-width: 749px)')
    : null;

  function isMobile() {
    return mobileQuery ? mobileQuery.matches : window.innerWidth <= 749;
  }

  /* Clip rectangles for a slide that is fully hidden off one edge. A wipe to
     the next slide starts the incoming slide hidden on the left and opens its
     right edge; going back mirrors that. */
  var CLIP = {
    hiddenLeft: 'inset(0 100% 0 0)',
    hiddenRight: 'inset(0 0 0 100%)',
    full: 'inset(0 0 0 0)'
  };

  function initSlider(root) {
    if (!root || root.dataset.heroReady === 'true') return;
    root.dataset.heroReady = 'true';

    var track = root.querySelector('[data-hero-track]');
    var slides = Array.prototype.slice.call(root.querySelectorAll('[data-hero-slide]'));
    var dots = Array.prototype.slice.call(root.querySelectorAll('[data-hero-dot]'));
    var prev = root.querySelector('[data-hero-prev]');
    var next = root.querySelector('[data-hero-next]');

    if (!track || !slides.length) return;

    var transition = root.dataset.transition || 'swipe';
    var duration = parseInt(root.dataset.transitionSpeed, 10) || 900;
    var autoplay = root.dataset.autoplay === 'true';
    var pauseHover = root.dataset.pauseHover === 'true';
    var intervalMs = parseInt(root.dataset.autoplaySpeed, 10) || 5000;

    if (reduceMotion) duration = 0;
    root.style.setProperty('--chs-dur', duration + 'ms');

    var count = slides.length;
    var hasLoop = count > 1;

    var current = 0;
    var timer = null;
    var releaseTimer = null;
    var resizeTimer = null;
    var rewindTimer = null;
    var animating = false;
    var pending = null;
    var inViewport = true;

    /* Which property finishing means the wipe is over. Each transition style
       animates a different one, so the listener has to watch the right one. */
    var settleProp = transition === 'fade'
      ? 'opacity'
      : (transition === 'slide' ? 'transform' : 'clip-path');

    /* Pointer drag state. A drag reveals the candidate slide progressively so
       the wipe tracks the finger, then either completes or rewinds on release. */
    var dragging = false;
    var pointerId = null;
    var startX = 0;
    var startY = 0;
    var lastX = 0;
    var axisLocked = false;
    var horizontal = false;
    var dragTarget = null;
    var dragDirection = 0;
    var suppressClick = false;
    var clickTolerance = 6;

    function wrap(index) {
      return ((index % count) + count) % count;
    }

    /* ---------- Painting ---------- */

    function setClip(slide, value) {
      slide.style.setProperty('--chs-clip', value);
    }

    function clearInline(slide) {
      slide.style.removeProperty('--chs-clip');
      slide.style.removeProperty('--chs-x');
      slide.style.removeProperty('--chs-op');
    }

    /* Put a slide back to its hidden resting state without animating it there.
       Forcing a reflow between the class toggles is what makes the reset
       instant rather than a visible rewind. */
    function reset(slide) {
      slide.classList.add('no-anim');
      slide.classList.remove('is-active', 'is-leaving');
      clearInline(slide);
      void slide.offsetWidth;
      slide.classList.remove('no-anim');
    }

    function hiddenClipFor(direction) {
      return direction > 0 ? CLIP.hiddenLeft : CLIP.hiddenRight;
    }

    /* The three transition styles differ only in what the incoming slide looks
       like before it settles: clipped to an edge, transparent, or offset. */
    function stageIncoming(slide, direction) {
      slide.classList.add('no-anim');

      if (transition === 'fade') {
        setClip(slide, CLIP.full);
        slide.style.setProperty('--chs-op', '0');
      } else if (transition === 'slide') {
        setClip(slide, CLIP.full);
        slide.style.setProperty('--chs-x', direction > 0 ? '100%' : '-100%');
      } else {
        setClip(slide, hiddenClipFor(direction));
      }

      void slide.offsetWidth;
      slide.classList.remove('no-anim');
    }

    function settleIncoming(slide) {
      clearInline(slide);
      slide.classList.add('is-active');
    }

    /* The outgoing slide holds still and fully drawn one layer down while the
       incoming one covers it. In "slide" mode it drifts the other way instead. */
    function stageOutgoing(slide, direction) {
      slide.classList.remove('is-active');
      slide.classList.add('is-leaving');
      clearInline(slide);

      if (transition === 'slide') {
        void slide.offsetWidth;
        slide.style.setProperty('--chs-x', direction > 0 ? '-100%' : '100%');
      }
    }

    /* ---------- Height ----------
       The banner height is never measured or written as a pixel value. Each
       slide carries the natural aspect ratio of its own media, rendered by
       Liquid into data-ratio-desktop / data-ratio-mobile. We copy the active
       slide's pair into --ratio-active / --ratio-active-m and let the
       stylesheet's aspect-ratio do the sizing, so the desktop image drives the
       desktop height and the mobile image drives the mobile height, with no
       layout shift and no circular offsetHeight read. */

    function ratioFromImage(slide) {
      var image = slide.querySelector('img.chs-image');
      if (!image || !image.naturalWidth || !image.naturalHeight) return null;
      return image.naturalWidth / image.naturalHeight;
    }

    function syncRatio() {
      var slide = slides[current];
      if (!slide) return;

      var desktop = slide.getAttribute('data-ratio-desktop');
      var mobile = slide.getAttribute('data-ratio-mobile');

      /* Fallback for slides saved before the ratio attributes existed: read the
         image that is actually rendered at this breakpoint. */
      if (!desktop || !mobile) {
        var measured = ratioFromImage(slide);
        if (measured) {
          if (!mobile && isMobile()) mobile = String(measured);
          if (!desktop && !isMobile()) desktop = String(measured);
        }
      }

      if (desktop) root.style.setProperty('--ratio-active', desktop);
      if (mobile) root.style.setProperty('--ratio-active-m', mobile);
    }

    function updateDots() {
      dots.forEach(function (dot, index) {
        var active = index === current;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-selected', active ? 'true' : 'false');
      });
    }

    /* Videos ship with preload="none" and no autoplay attribute, so nothing is
       fetched until this runs for the slide that is actually on screen. The
       slide passed as `keep` is the one being wiped away: it is still visible
       under the incoming slide, so its video keeps running until the wipe ends
       rather than freezing halfway through. */
    function updateVideos(keep) {
      var activeSlide = slides[current];

      slides.forEach(function (slide) {
        slide.querySelectorAll('video').forEach(function (video) {
          var visible = window.getComputedStyle(video).display !== 'none';

          if (slide === activeSlide && visible && inViewport) {
            video.muted = true;
            if (video.preload === 'none') video.preload = 'auto';
            var promise = video.play();
            if (promise && typeof promise.catch === 'function') {
              promise.catch(function () {});
            }
          } else if (slide !== keep && !video.paused) {
            video.pause();
          }
        });
      });
    }

    function updateA11y() {
      slides.forEach(function (slide, index) {
        var active = index === current;
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');
        slide.querySelectorAll('a, button').forEach(function (el) {
          if (active) el.removeAttribute('tabindex');
          else el.setAttribute('tabindex', '-1');
        });
      });
    }

    function updateUI(keepVideo) {
      updateDots();
      syncRatio();
      updateVideos(keepVideo);
      updateA11y();
    }

    /* ---------- Transition ---------- */

    /* Tidy up after a wipe: park the slide that was uncovered and let the next
       gesture through. Safe to call twice, because whichever of the
       transitionend listener and the fallback timer arrives first clears the
       other and nulls `pending`. */
    function finishTransition() {
      if (!pending) return;

      var done = pending;
      pending = null;
      animating = false;

      window.clearTimeout(releaseTimer);
      done.incoming.removeEventListener('transitionend', done.handler);

      reset(done.outgoing);
      done.incoming.classList.add('is-active');
      updateVideos();
    }

    function goTo(index, direction) {
      index = wrap(index);

      /* One wipe at a time. Without this a second click part way through the
         first one starts a competing transition, and two slides travel at
         once. Extra clicks are dropped, not queued, so a double click on an
         arrow advances exactly one slide. */
      if (animating || index === current) return;

      var outgoing = slides[current];
      var incoming = slides[index];

      if (!direction) {
        direction = index > current ? 1 : -1;
        /* Wrapping around should keep travelling the way it was going. */
        if (current === 0 && index === count - 1) direction = -1;
        if (current === count - 1 && index === 0) direction = 1;
      }

      animating = true;
      current = index;

      slides.forEach(function (slide) {
        if (slide !== outgoing && slide !== incoming) reset(slide);
      });

      stageOutgoing(outgoing, direction);
      stageIncoming(incoming, direction);
      settleIncoming(incoming);

      /* The uncovered slide stays on screen for the whole wipe, so its video is
         left running and its dot/aria state is updated straight away. */
      updateUI(outgoing);

      function onEnd(event) {
        if (event.target !== incoming || event.propertyName !== settleProp) return;
        finishTransition();
      }

      pending = { outgoing: outgoing, incoming: incoming, handler: onEnd };
      incoming.addEventListener('transitionend', onEnd);

      /* Fallback for the cases transitionend never fires: a zero duration under
         reduced motion, or a backgrounded tab. */
      releaseTimer = window.setTimeout(finishTransition, duration + 120);
    }

    function nextSlide() { if (hasLoop) goTo(current + 1, 1); }
    function prevSlide() { if (hasLoop) goTo(current - 1, -1); }

    /* ---------- Autoplay ---------- */

    function stopAutoplay() {
      if (timer) {
        window.clearInterval(timer);
        timer = null;
      }
    }

    function startAutoplay() {
      stopAutoplay();
      if (!autoplay || reduceMotion || !hasLoop || dragging || !inViewport) return;
      timer = window.setInterval(nextSlide, intervalMs);
    }

    /* ---------- Drag ----------
       Mouse and touch share one pointer path. The vertical axis is left to the
       page: until the gesture proves itself horizontal we do not touch it, so
       a scroll that starts on the banner still scrolls. */

    function dragWidth() {
      return track.clientWidth || 1;
    }

    function beginDragPreview(direction) {
      if (dragTarget && dragDirection === direction) return;

      /* A rewind from a previous half-drag may still be settling. Cancel it,
         otherwise its timeout fires mid-gesture and blanks the slide the
         finger is currently revealing. */
      window.clearTimeout(rewindTimer);

      if (dragTarget) reset(dragTarget);

      dragDirection = direction;
      dragTarget = slides[wrap(current + direction)];
      if (dragTarget === slides[current]) {
        dragTarget = null;
        return;
      }

      dragTarget.classList.add('no-anim');
      dragTarget.style.zIndex = '3';
      if (transition === 'fade') {
        setClip(dragTarget, CLIP.full);
        dragTarget.style.setProperty('--chs-op', '0');
      } else if (transition === 'slide') {
        setClip(dragTarget, CLIP.full);
        dragTarget.style.setProperty('--chs-x', direction > 0 ? '100%' : '-100%');
      } else {
        setClip(dragTarget, hiddenClipFor(direction));
      }
    }

    function paintDragPreview(progress) {
      if (!dragTarget) return;

      var pct = Math.max(0, Math.min(1, progress));

      if (transition === 'fade') {
        dragTarget.style.setProperty('--chs-op', String(pct));
      } else if (transition === 'slide') {
        var offset = (1 - pct) * 100 * (dragDirection > 0 ? 1 : -1);
        dragTarget.style.setProperty('--chs-x', offset + '%');
      } else {
        var remaining = (1 - pct) * 100;
        setClip(
          dragTarget,
          dragDirection > 0
            ? 'inset(0 ' + remaining + '% 0 0)'
            : 'inset(0 0 0 ' + remaining + '%)'
        );
      }
    }

    function clearDragPreview() {
      if (!dragTarget) return;
      var target = dragTarget;
      dragTarget = null;
      dragDirection = 0;
      target.style.removeProperty('z-index');
      target.classList.remove('no-anim');
      reset(target);
    }

    function onPointerDown(event) {
      if (!hasLoop || animating) return;
      if (event.pointerType === 'mouse' && event.button !== 0) return;

      dragging = true;
      axisLocked = false;
      horizontal = false;
      suppressClick = false;
      pointerId = event.pointerId;
      startX = event.clientX;
      startY = event.clientY;
      lastX = event.clientX;

      stopAutoplay();
    }

    function onPointerMove(event) {
      if (!dragging || event.pointerId !== pointerId) return;

      var dx = event.clientX - startX;
      var dy = event.clientY - startY;

      if (!axisLocked) {
        if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
        axisLocked = true;
        horizontal = Math.abs(dx) > Math.abs(dy);
        if (!horizontal) {
          dragging = false;
          startAutoplay();
          return;
        }
        track.classList.add('is-mouse-dragging');
        if (track.setPointerCapture) {
          try { track.setPointerCapture(pointerId); } catch (error) { /* ignore */ }
        }
      }

      if (!horizontal) return;

      lastX = event.clientX;
      beginDragPreview(dx < 0 ? 1 : -1);
      paintDragPreview(Math.abs(dx) / dragWidth());
      if (event.cancelable) event.preventDefault();
    }

    function onPointerUp(event) {
      if (!dragging || (pointerId !== null && event.pointerId !== pointerId)) return;

      var dx = lastX - startX;
      dragging = false;
      pointerId = null;
      track.classList.remove('is-mouse-dragging');
      suppressClick = horizontal && Math.abs(dx) > clickTolerance;

      var committed = horizontal &&
        Math.abs(dx) > Math.min(120, dragWidth() * 0.18);

      if (committed) {
        var direction = dx < 0 ? 1 : -1;
        clearDragPreview();
        goTo(current + direction, direction);
      } else {
        /* Let the half-revealed slide animate back out of frame. */
        if (dragTarget) {
          dragTarget.classList.remove('no-anim');
          void dragTarget.offsetWidth;
          paintDragPreview(0);
          var rewinding = dragTarget;
          dragTarget = null;
          dragDirection = 0;
          window.clearTimeout(rewindTimer);
          rewindTimer = window.setTimeout(function () {
            rewinding.style.removeProperty('z-index');
            reset(rewinding);
          }, duration + 60);
        }
      }

      horizontal = false;
      startAutoplay();
    }

    if (window.PointerEvent) {
      track.addEventListener('pointerdown', onPointerDown);
      track.addEventListener('pointermove', onPointerMove, { passive: false });
      track.addEventListener('pointerup', onPointerUp);
      track.addEventListener('pointercancel', onPointerUp);
      track.addEventListener('lostpointercapture', onPointerUp);
    }

    track.addEventListener('dragstart', function (event) {
      event.preventDefault();
    });

    /* ---------- Clicks ---------- */

    track.addEventListener('click', function (event) {
      if (suppressClick) {
        event.preventDefault();
        event.stopPropagation();
        suppressClick = false;
        return;
      }

      if (event.target.closest('.chs-button, .chs-text a, .chs-arrow, .chs-dot')) return;

      var slide = event.target.closest('[data-hero-slide]');
      if (!slide || slide !== slides[current]) return;

      var href = slide.getAttribute('data-banner-href');
      if (!href) return;

      if (slide.getAttribute('data-banner-target') === '_blank') {
        window.open(href, '_blank', 'noopener');
      } else {
        window.location.href = href;
      }
    }, true);

    if (next) {
      next.addEventListener('click', function () {
        stopAutoplay();
        nextSlide();
        startAutoplay();
      });
    }

    if (prev) {
      prev.addEventListener('click', function () {
        stopAutoplay();
        prevSlide();
        startAutoplay();
      });
    }

    dots.forEach(function (dot, index) {
      dot.addEventListener('click', function () {
        stopAutoplay();
        goTo(index);
        startAutoplay();
      });
    });

    root.addEventListener('keydown', function (event) {
      if (event.key === 'ArrowRight') {
        stopAutoplay(); nextSlide(); startAutoplay();
      } else if (event.key === 'ArrowLeft') {
        stopAutoplay(); prevSlide(); startAutoplay();
      }
    });

    if (pauseHover) {
      root.addEventListener('mouseenter', function () {
        if (!dragging) stopAutoplay();
      });

      root.addEventListener('mouseleave', function () {
        if (!dragging) startAutoplay();
      });
    }

    document.addEventListener('visibilitychange', function () {
      if (document.hidden) stopAutoplay();
      else startAutoplay();
    });

    /* No timers, no video playback while the hero is scrolled out of view. */
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        inViewport = entries[0].isIntersecting;
        if (inViewport) startAutoplay();
        else stopAutoplay();
        updateVideos();
      }, { threshold: 0.15 }).observe(root);
    }

    window.addEventListener('resize', function () {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(function () {
        syncRatio();
        updateVideos();
      }, 150);
    }, { passive: true });

    /* Crossing the 750px breakpoint swaps which image is rendered, so re-read
       the ratio for the new breakpoint. */
    if (mobileQuery) {
      if (typeof mobileQuery.addEventListener === 'function') {
        mobileQuery.addEventListener('change', syncRatio);
      } else if (typeof mobileQuery.addListener === 'function') {
        mobileQuery.addListener(syncRatio);
      }
    }

    slides.forEach(function (slide) {
      slide.querySelectorAll('img').forEach(function (image) {
        if (!image.complete) {
          image.addEventListener('load', syncRatio, { once: true });
        }
      });
    });

    root.heroSliderApi = {
      goToSlide: function (index) {
        stopAutoplay();
        /* Selecting a block in the theme editor should always land, so cut any
           wipe that happens to be mid-flight rather than dropping the jump. */
        finishTransition();
        goTo(index);
      }
    };

    /* First paint: slide zero on screen with no wipe, everything else parked. */
    slides.forEach(function (slide, index) {
      slide.classList.add('no-anim');
      slide.classList.toggle('is-active', index === 0);
      clearInline(slide);
    });
    void track.offsetWidth;
    root.classList.add('is-ready');
    slides.forEach(function (slide) { slide.classList.remove('no-anim'); });

    updateUI();
    startAutoplay();
  }

  function initAll(container) {
    (container || document)
      .querySelectorAll('[data-custom-hero-slider]')
      .forEach(initSlider);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll(document);
    });
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });

  document.addEventListener('shopify:block:select', function (event) {
    var slide = event.target.closest('[data-hero-slide]');
    var root = event.target.closest('[data-custom-hero-slider]');

    if (!slide || !root || !root.heroSliderApi) return;

    root.heroSliderApi.goToSlide(parseInt(slide.dataset.slideIndex, 10) || 0);
  });
}());